import React from 'react';
import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
  jsonLd?: object;
  
  // Article specific props
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
}

const SEO: React.FC<SEOProps> = ({ 
  title, 
  description, 
  canonical, 
  ogImage,
  ogType = 'website', 
  twitterCard = 'summary_large_image',
  jsonLd,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags
}) => {
  const siteTitle = 'LendLifted';
  const fullTitle = title === siteTitle ? title : `${title} | ${siteTitle}`;
  const envSiteUrl = import.meta.env.VITE_SITE_URL || import.meta.env.VITE_APP_URL;
  const siteUrl = envSiteUrl ? new URL(envSiteUrl).origin : 'https://lendlifted.com';
  const canonicalUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  
  // Fallback to default site image if none provided
  const imageToUse = ogImage || '/og-image.png';
  const absoluteOgImage = imageToUse.startsWith('http') ? imageToUse : `${siteUrl}${imageToUse.startsWith('/') ? '' : '/'}${imageToUse}`;

  const allowIndexingEnv = import.meta.env.VITE_ALLOW_INDEXING;
  const shouldIndex = allowIndexingEnv === 'true';

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {!shouldIndex && (
        <meta name="robots" content="noindex, nofollow" />
      )}

      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={absoluteOgImage} />
      <meta property="og:site_name" content={siteTitle} />

      {ogType === 'article' && articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
      {ogType === 'article' && articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
      {ogType === 'article' && articleSection && <meta property="article:section" content={articleSection} />}
      {ogType === 'article' && articleTags?.map(tag => (
        <meta property="article:tag" content={tag} key={tag} />
      ))}

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteOgImage} />
      <meta name="twitter:creator" content="@LendLifted" /> 

      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
